import React, { useMemo } from 'react';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { chartColors } from '../../config/theme';
import { useTheme } from '../../contexts/ThemeContext';
import { utcHourLabel } from '../../utils/flow';
import { formatUtc } from '../../utils/dateWindow';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export interface HourlyBarSeries {
  key: string;
  label: string;
  /** One value per slot; null = no figure for that hour (no bar). */
  values: readonly (number | null)[];
  color: string;
}

export interface HourlyBarChartProps {
  /** UTC ms of each hour start. */
  slots: readonly number[];
  series: HourlyBarSeries[];
  yLabel: string;
  ariaLabel: string;
}

const fmt = (n: number) => n.toLocaleString('en-GB');

/** Grouped bars per UTC hour, with the same figures in a table underneath for screen readers. */
const HourlyBarChart: React.FC<HourlyBarChartProps> = ({ slots, series, yLabel, ariaLabel }) => {
  const { isDark } = useTheme();
  const colors = chartColors(isDark);

  const data = useMemo(
    () => ({
      labels: slots.map(utcHourLabel),
      datasets: series.map((s) => ({
        label: s.label,
        data: s.values.slice() as (number | null)[],
        backgroundColor: s.color,
        borderRadius: 2,
      })),
    }),
    [slots, series],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: { display: true, text: 'Hour (UTC)', color: colors.tick },
          ticks: { color: colors.tick, maxRotation: 0, autoSkip: true },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: yLabel, color: colors.tick },
          ticks: { color: colors.tick },
          grid: { color: colors.grid },
        },
      },
      plugins: {
        legend: { labels: { color: colors.tick } },
        tooltip: {
          backgroundColor: colors.tooltipBg,
          callbacks: {
            title: (items: Array<{ dataIndex: number }>) =>
              items[0] ? formatUtc(slots[items[0].dataIndex], 'hourly') : '',
            label: (item: { dataset: { label?: string }; parsed: { y: number | null } }) =>
              item.parsed.y == null ? `${item.dataset.label}: no data` : `${item.dataset.label}: ${fmt(item.parsed.y)} people`,
          },
        },
      },
    }),
    [colors.tick, colors.grid, colors.tooltipBg, yLabel, slots],
  );

  return (
    <div>
      <div className="h-56 w-full">
        <Bar data={data} options={options as any} role="img" aria-label={ariaLabel} />
      </div>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer text-ink-muted">Show hourly figures</summary>
        <div className="mt-2 max-h-64 overflow-y-auto">
          <table className="table table-xs">
            <thead>
              <tr>
                <th>Hour (UTC)</th>
                {series.map((s) => (
                  <th key={s.key}>{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((ms, i) => (
                <tr key={ms}>
                  <td>{utcHourLabel(ms)}</td>
                  {series.map((s) => (
                    <td key={s.key}>{s.values[i] == null ? '–' : fmt(s.values[i] as number)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};

export default HourlyBarChart;
